import type { ReactNode } from 'react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

import type { ListDensity } from '../store/list-view.store';

import { ProductThumb } from './product-thumb';

/**
 * Card sizes per density — a minimum card width the grid auto-fills, and a fixed image box
 * height (object-contain; `!` because the thumb's enlarge button sets `h-auto`) so every card
 * in a row has the same skeleton. Standard fits ~8 cards
 * on a wide product-master list; compact ~10; spacious ~6.
 */
const CARD_DENSITY = {
  compact: {
    min: '150px',
    imageClass: '!h-24',
    imagePx: 150,
    cardClass: 'gap-1 p-2',
    titleClass: 'min-h-8 text-xs leading-4',
  },
  normal: {
    min: '190px',
    imageClass: '!h-[140px]',
    imagePx: 190,
    cardClass: 'gap-1.5 p-2',
    titleClass: 'min-h-10 text-sm leading-5',
  },
  spacious: {
    min: '240px',
    imageClass: '!h-[190px]',
    imagePx: 240,
    cardClass: 'gap-2 p-3',
    titleClass: 'min-h-10 text-sm leading-5',
  },
} as const satisfies Record<
  ListDensity,
  { min: string; imageClass: string; imagePx: number; cardClass: string; titleClass: string }
>;

/** What one product card shows; every slot except title is optional. */
export interface ProductCardItem {
  key: string;
  title: string;
  imageUrl?: string | null;
  /** Full-size image when `imageUrl` is a small rendition: drawn card-wide and opened in the viewer. */
  fullImageUrl?: string | null;
  gallery?: (string | null | undefined)[];
  /** Muted second line: offer id, brand, ids. */
  subtitle?: ReactNode;
  /** Small market chips at the end of the second line. */
  chips?: ReactNode;
  /** Status / flag chips — overlaid on the image's top-left corner so every card body keeps
   *  the same structure whether or not a card has a badge. */
  badges?: ReactNode;
  /** Footer, left: the key metric (price, score, outcome). */
  metric?: ReactNode;
  /** Footer, right: a quiet secondary fact (availability dot, market count…). */
  aside?: ReactNode;
  /** An extra wrapped row of small chips above the footer (e.g. this run's per-stage state) —
   *  its own line so it never crowds the metric/aside footer or wraps mid-word. */
  stageChips?: ReactNode;
  /** ⋯ menu or a small action, top-right of the image. */
  actions?: ReactNode;
  onOpen?: () => void;
}

interface Props {
  items: ProductCardItem[];
  density: ListDensity;
  /** Overrides the density's minimum card width (e.g. "220px" for the batch Products cards). */
  minCardWidth?: string;
  className?: string;
}

/**
 * Auto-fill card grid for product lists: column count follows the available width and the
 * density's minimum card width. Every card has the same skeleton — a fixed-height image box
 * (badges overlaid), a 2-line title, one muted id + market-chips line, then ONE footer line
 * (metric left, aside right; it wraps only when the two truly don't fit) — so a row lines up.
 */
export function ProductCardGrid({ items, density, minCardWidth, className }: Props) {
  const d = CARD_DENSITY[density];
  const min = minCardWidth ?? d.min;
  return (
    <ul
      className={cn('grid gap-2', className)}
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}, 100%), 1fr))` }}
    >
      {items.map((item) => (
        <li
          key={item.key}
          className={cn(
            'bg-card relative flex h-full min-w-0 flex-col rounded-lg border transition-shadow hover:shadow-sm',
            d.cardClass,
          )}
        >
          {/* Image sits above the stretched link so clicking it still enlarges the image. */}
          <div className="relative z-10">
            <ProductThumb
              src={item.fullImageUrl ?? item.imageUrl}
              title={item.title}
              gallery={item.gallery}
              pixelSize={d.imagePx}
              className={cn(
                'bg-background size-auto w-full rounded-md [&_img]:object-contain [&_img]:p-0.5',
                d.imageClass,
              )}
            />
            {item.badges ? (
              <div className="pointer-events-none absolute top-1 left-1 flex max-w-[calc(100%-2.5rem)] flex-wrap gap-1 [&>*]:pointer-events-auto [&>*]:shadow-sm">
                {item.badges}
              </div>
            ) : null}
            {item.actions ? <div className="absolute top-1 right-1">{item.actions}</div> : null}
          </div>
          {item.onOpen ? (
            // Stretched link: the title button's ::after covers the card, so the whole
            // card opens the product while staying a real, focusable button.
            <Button
              type="button"
              variant="link"
              onClick={item.onOpen}
              title={item.title}
              className={cn(
                'text-foreground h-auto cursor-pointer items-start justify-start p-0 text-left font-medium whitespace-normal after:absolute after:inset-0 after:content-[""]',
                d.titleClass,
              )}
            >
              <span className="line-clamp-2">{item.title}</span>
            </Button>
          ) : (
            <p
              title={item.title}
              className={cn('text-foreground line-clamp-2 font-medium', d.titleClass)}
            >
              {item.title}
            </p>
          )}
          <div className="text-muted-foreground flex min-h-4 min-w-0 items-center gap-1.5 text-xs leading-4">
            {item.subtitle ? <span className="min-w-0 truncate">{item.subtitle}</span> : null}
            {item.chips ? (
              <span className="relative z-10 ml-auto flex shrink-0 items-center gap-1">
                {item.chips}
              </span>
            ) : null}
          </div>
          {item.stageChips ? (
            // `max-w-full` per chip: a long chip truncates inside the card instead of overflowing it.
            <div className="relative z-10 flex min-w-0 flex-wrap items-center gap-1 text-xs [&>*]:max-w-full">
              {item.stageChips}
            </div>
          ) : null}
          {item.metric || item.aside ? (
            // ONE line in normal flow: metric left, aside right; flex-wrap drops the aside to a
            // second line only when both can't fit — nothing absolutely positioned, no overlap.
            <div
              className={cn(
                'mt-auto flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-0.5 border-t pt-1.5',
                density === 'compact' ? 'text-xs' : 'text-sm',
              )}
            >
              {item.metric ? <div className="relative z-10 min-w-0">{item.metric}</div> : null}
              {item.aside ? <div className="relative z-10 min-w-0">{item.aside}</div> : null}
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
